import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Clock, CheckCircle, XCircle, Eye, FunnelSimple } from "@phosphor-icons/react";

const Applications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const res = await axios.get(`${API}/applications`);
        setApplications(res.data.applications || []);
      } catch (error) {
        console.error("Error fetching applications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400" />;
      case "rejected":
        return <XCircle weight="fill" className="w-5 h-5 text-red-400" />;
      case "submitted":
      case "under_review":
        return <Clock weight="fill" className="w-5 h-5 text-yellow-400" />;
      default:
        return <FileText weight="fill" className="w-5 h-5 text-zinc-400" />;
    }
  };

  const filteredApplications = statusFilter 
    ? applications.filter(app => app.status === statusFilter)
    : applications;

  const statusCounts = {
    all: applications.length,
    draft: applications.filter(a => a.status === "draft").length,
    submitted: applications.filter(a => a.status === "submitted").length,
    under_review: applications.filter(a => a.status === "under_review").length,
    approved: applications.filter(a => a.status === "approved").length,
    rejected: applications.filter(a => a.status === "rejected").length
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="applications-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Applications</h1>
            <p className="text-zinc-400 mt-1">Track your grant applications</p>
          </div>
          <Button 
            onClick={() => navigate("/grants")}
            className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
            data-testid="new-application-btn"
          >
            Find New Grants
          </Button>
        </div>

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2">
          <Button 
            variant={statusFilter === "" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("")}
            className={statusFilter === "" ? "bg-emerald-500 text-black" : "border-zinc-700"}
            data-testid="filter-all"
          >
            All ({statusCounts.all})
          </Button>
          <Button 
            variant={statusFilter === "draft" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("draft")}
            className={statusFilter === "draft" ? "bg-zinc-600 text-white" : "border-zinc-700"}
            data-testid="filter-draft"
          >
            Draft ({statusCounts.draft})
          </Button>
          <Button 
            variant={statusFilter === "submitted" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("submitted")}
            className={statusFilter === "submitted" ? "bg-blue-600 text-white" : "border-zinc-700"}
            data-testid="filter-submitted"
          >
            Submitted ({statusCounts.submitted})
          </Button>
          <Button 
            variant={statusFilter === "under_review" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("under_review")}
            className={statusFilter === "under_review" ? "bg-yellow-600 text-black" : "border-zinc-700"}
            data-testid="filter-under-review"
          >
            Under Review ({statusCounts.under_review})
          </Button>
          <Button 
            variant={statusFilter === "approved" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("approved")}
            className={statusFilter === "approved" ? "bg-emerald-500 text-black" : "border-zinc-700"}
            data-testid="filter-approved"
          >
            Approved ({statusCounts.approved})
          </Button>
          <Button 
            variant={statusFilter === "rejected" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("rejected")}
            className={statusFilter === "rejected" ? "bg-red-600 text-white" : "border-zinc-700"}
            data-testid="filter-rejected"
          >
            Rejected ({statusCounts.rejected})
          </Button>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="text-center py-16">
            <FileText weight="duotone" className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {statusFilter ? "No applications with this status" : "No applications yet"}
            </h3>
            <p className="text-zinc-500 mb-4">
              {statusFilter ? "Try a different filter" : "Start by browsing available grants"}
            </p>
            {!statusFilter && (
              <Button 
                onClick={() => navigate("/grants")}
                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                data-testid="browse-grants-btn"
              >
                Browse Grants
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => (
              <Card 
                key={app.application_id}
                className="bg-zinc-950 border-zinc-800 card-hover"
                data-testid={`application-${app.application_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(app.status)}
                      <div>
                        <h3 className="font-semibold text-lg">{app.grant_title}</h3>
                        <div className="flex items-center gap-4 text-sm text-zinc-400 mt-1">
                          <span>Created: {new Date(app.created_at).toLocaleDateString()}</span>
                          {app.submitted_at && (
                            <span>Submitted: {new Date(app.submitted_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`status-${app.status} px-3 py-1 rounded-sm text-xs font-mono uppercase`}>
                        {app.status.replace("_", " ")}
                      </span>
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/grants/${app.grant_id}`)}
                        className="border-zinc-700"
                        data-testid={`view-application-${app.application_id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </div>

                  {/* Progress Bar for Draft */}
                  {app.status === "draft" && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                        <span>Application Progress</span>
                        <span>{app.draft_content ? "In Progress" : "Not Started"}</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: app.draft_content ? "50%" : "0%" }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Applications;
